import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState } from '@/components/ui/States';
import { getBillingSettings, updateBillingSettings } from '../api';
import type { BillingSettings } from '../types';

interface OptionDraft {
  id: number;
  value: string;
}

let optionSeq = 0;
const toDraft = (options: number[]): OptionDraft[] => options.map((v) => ({ id: ++optionSeq, value: String(v) }));

/**
 * Centralized GST configuration: the On/Off toggle, plus the list of
 * percentages every GST % dropdown offers. Backing table (billing_settings)
 * already existed for invoice_numbering_mode (see migration_branches.sql)
 * but had no UI/API — this tab is that settings screen.
 *
 * Turning GST off here hides GST fields and zeroes GST calculation on every
 * PATIENT-facing bill created from that point on — OPD (BillingModule.tsx/
 * BillingStage.jsx, incl. Other Charges), Pharmacy dispensing
 * (DispensingTab.tsx/PharmacyStage.jsx), and IP room rent + ad-hoc charges
 * at discharge (IPModule.tsx) — see useBillingSettings.js's consumers.
 * Goods Receipt (pharmacy procurement) is deliberately NOT gated by this
 * toggle: it records input tax on what the hospital pays a vendor, which is
 * independent of whether output GST is currently charged to patients.
 *
 * Editing gst_percentage_options only changes what every one of those
 * dropdowns OFFERS (including Goods Receipt) — records already created keep
 * whatever percentage they were built with, since the backend snapshots
 * gst_percent/gst_amount per line rather than deriving it live, so neither
 * control ever rewrites history.
 */
export default function BillingSettingsTab() {
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [optionsDraft, setOptionsDraft] = useState<OptionDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [savingOptions, setSavingOptions] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [optionsSaved, setOptionsSaved] = useState(false);

  useEffect(() => {
    getBillingSettings()
      .then((s) => {
        setSettings(s);
        setOptionsDraft(toDraft(s.gst_percentage_options));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const toggleGst = async (checked: boolean) => {
    if (!settings) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const updated = await updateBillingSettings({ gst_enabled: checked });
      setSettings(updated);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setSaving(false);
  };

  const addOption = () => setOptionsDraft((prev) => [...prev, { id: ++optionSeq, value: '0' }]);
  const updateOption = (id: number, value: string) => setOptionsDraft((prev) => prev.map((o) => (o.id === id ? { ...o, value } : o)));
  const removeOption = (id: number) => setOptionsDraft((prev) => prev.filter((o) => o.id !== id));

  const saveOptions = async () => {
    const values = optionsDraft.map((o) => parseFloat(o.value)).filter((v) => Number.isFinite(v) && v >= 0 && v <= 100);
    if (!values.length) { setError('Add at least one valid GST percentage (0–100).'); return; }
    setSavingOptions(true); setError(''); setOptionsSaved(false);
    try {
      const updated = await updateBillingSettings({ gst_percentage_options: values });
      setSettings(updated);
      setOptionsDraft(toDraft(updated.gst_percentage_options));
      setOptionsSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setSavingOptions(false);
  };

  if (!settings) {
    return error ? (
      <div className="rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>
    ) : (
      <LoadingState />
    );
  }

  return (
    <Card style={{ padding: 16, maxWidth: 560 }}>
      <div className="mb-1 text-sm font-semibold">GST</div>
      <div className="mb-4 text-xs text-ink-muted">
        One centralized switch for GST across every patient-facing bill — <b>OPD billing</b> (registration,
        consultation, procedure, Other Charges), <b>Pharmacy billing</b> (medicine dispensing), and{' '}
        <b>Room Rent / IP charges</b> at discharge. Turning it off hides every GST field on those screens and stops
        GST from being calculated on <b>bills created from now on</b>. Bills already generated are never changed,
        whichever way GST was set when they were created. This switch does not affect Goods Receipt (Pharmacy →
        Procurement) — that records the tax the hospital pays a vendor, separate from what's charged to patients.
      </div>

      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <div className="flex items-center justify-between rounded-[var(--radius)] border border-line px-3.5 py-3">
        <div>
          <div className="text-[13px] font-semibold">GST on new bills</div>
          <div className="text-[11px] text-ink-muted">
            {settings.gst_enabled ? 'On — GST fields shown, GST applied as usual.' : 'Off — GST fields hidden, no GST is charged.'}
          </div>
        </div>
        <Switch checked={settings.gst_enabled} disabled={saving} onCheckedChange={toggleGst} />
      </div>
      {saved && !error && (
        <div className="mt-2 rounded-[var(--radius)] border border-success-soft bg-success-soft px-3 py-2 text-xs text-success-ink">
          ✓ Saved.
        </div>
      )}

      <div className="mt-5 border-t border-line pt-4">
        <div className="mb-1 text-sm font-semibold">GST Percentage Options</div>
        <div className="mb-3 text-xs text-ink-muted">
          The values available in every GST % dropdown across the app: <b>OPD billing</b> (incl. Other Charges),{' '}
          <b>Pharmacy billing</b>, <b>Room Rent / IP charges</b> at discharge, and{' '}
          <b>Pharmacy → Procurement → Goods Receipt</b>. Add, edit or remove values here — every dropdown picks up
          the change immediately for anything created from now on. Bills and receipts already generated keep
          whatever percentage they were created with.
        </div>

        {optionsDraft.map((o) => (
          <div key={o.id} className="mb-2 flex items-end gap-2">
            <FormField label="GST %">
              <Input type="number" min={0} max={100} value={o.value} onChange={(e) => updateOption(o.id, e.target.value)} className="w-28" />
            </FormField>
            <Button variant="danger" size="icon" onClick={() => removeOption(o.id)}>
              ✕
            </Button>
          </div>
        ))}
        {optionsDraft.length === 0 && (
          <div className="mb-2 text-[11px] text-[#9CA3AF]">No GST percentages configured — add at least one.</div>
        )}

        <div className="mt-2 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={addOption}>
            + Add value
          </Button>
          <Button size="sm" disabled={savingOptions} loading={savingOptions} onClick={saveOptions}>
            {savingOptions ? 'Saving…' : 'Save GST percentages'}
          </Button>
        </div>
        {optionsSaved && !error && (
          <div className="mt-2 rounded-[var(--radius)] border border-success-soft bg-success-soft px-3 py-2 text-xs text-success-ink">
            ✓ Saved.
          </div>
        )}
      </div>

      {settings.updated_by && (
        <div className="mt-3 text-[11px] text-ink-muted">
          Last changed by {settings.updated_by} on {new Date(settings.updated_at).toLocaleString('en-IN')}
        </div>
      )}
    </Card>
  );
}
